import React, { useState, useEffect } from "react";
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  FileText,
  KeyRound,
  IndianRupee,
  UploadCloud,
  X,
  ShieldCheck,
  Globe,
  CreditCard,
  CheckCircle2,
  Lock,
  ChevronDown,
  Eye,
  EyeOff,
} from "lucide-react";
import { motion } from "framer-motion";
import type { InsertHotelRequest } from "@repo/types";
import toast from "react-hot-toast";
import { getUploadImageUrl } from "@repo/api-client";
import Select from "react-select";
import { GetCountries, GetState, GetCity } from "react-country-state-city";
import { COUNTRY_CODES } from "@repo/utils";

// Minimal types matching the library's actual shape
type CountryType = { id: number; name: string; [key: string]: any };
type StateType   = { id: number; name: string; [key: string]: any };
type CityType    = { id: number; name: string; [key: string]: any };

// Country option type — carries flagUrl for rendering
type CountryOption = { value: number; label: string; flagUrl: string };

// ===================== SECTION TITLE COMPONENT =====================
const SectionTitle = ({
  icon: Icon,
  title,
  description,
}: {
  icon: any;
  title: string;
  description: string;
}) => (
  <div className="flex items-center gap-4 mb-5 relative z-10">
    <div className="p-3 bg-slate-900 rounded-xl text-white shadow-sm">
      <Icon size={24} />
    </div>
    <div>
      <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 tracking-tight">
        {title}
      </h2>
      <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
        {description}
      </p>
    </div>
  </div>
);

// ===================== INPUT FIELD COMPONENT =====================
const InputField = ({
  label,
  name,
  value,
  onChange,
  type = "text",
  placeholder,
  icon: Icon,
  multiline = false,
  showToggle = false,
  onToggleShow,
}: any) => (
  <div className="mb-4 space-y-2 group">
    <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-1">
      {label}
    </label>

    <div className="relative group">
      {Icon && (
        <div
          className={`absolute left-4 ${
            multiline ? "top-4" : "top-1/2 -translate-y-1/2"
          } text-slate-400 dark:text-slate-500 group-focus-within:text-blue-600 dark:group-focus-within:text-blue-400 transition-colors z-10`}
        >
          <Icon size={18} />
        </div>
      )}

      {multiline ? (
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          className={`w-full bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 rounded-xl py-3 focus:bg-white dark:focus:bg-[#0b1437] focus:ring-4 focus:ring-blue-600/10 dark:focus:ring-white/5 focus:border-blue-500 dark:focus:border-white/20 outline-none transition-all duration-300 font-medium text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 min-h-[120px] ${
            Icon ? "px-11" : "px-4"
          }`}
          placeholder={placeholder}
        />
      ) : (
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          className={`w-full bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 rounded-xl py-3 focus:bg-white dark:focus:bg-[#0b1437] focus:ring-4 focus:ring-blue-600/10 dark:focus:ring-white/5 focus:border-blue-500 dark:focus:border-white/20 outline-none transition-all duration-300 font-medium text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 ${
            Icon ? (showToggle ? "px-11 pr-11" : "px-11") : showToggle ? "px-4 pr-11" : "px-4"
          }`}
          placeholder={placeholder}
        />
      )}

      {showToggle && !multiline && (
        <button
          type="button"
          onClick={onToggleShow}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors z-10 focus:outline-none"
          tabIndex={-1}
        >
          {type === "password" ? <Eye size={18} /> : <EyeOff size={18} />}
        </button>
      )}
    </div>
  </div>
);

type Props = {
  hotelRequest: InsertHotelRequest;
  setHotelRequest: React.Dispatch<React.SetStateAction<InsertHotelRequest>>;
  handleSave: () => void;
  loading: boolean;
};

// ===================== REACT-SELECT DARK/LIGHT STYLES =====================
const buildSelectStyles = (isDark: boolean) => ({
  control: (base: any, state: any) => ({
    ...base,
    background: state.isDisabled
      ? (isDark ? "rgba(27,37,75,0.3)" : "#f1f5f9")
      : (isDark ? "rgba(27,37,75,0.5)" : "#f8fafc"),
    borderColor: state.isFocused
      ? (isDark ? "rgba(255,255,255,0.2)" : "#3b82f6")
      : (isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"),
    borderRadius: "0.75rem",
    minHeight: "46px",
    boxShadow: state.isFocused
      ? (isDark ? "0 0 0 4px rgba(255,255,255,0.05)" : "0 0 0 4px rgba(59,130,246,0.1)")
      : "none",
    cursor: state.isDisabled ? "not-allowed" : "default",
    transition: "all 0.2s",
    "&:hover": {
      borderColor: isDark ? "rgba(255,255,255,0.2)" : "#3b82f6",
    },
  }),
  singleValue: (base: any) => ({
    ...base,
    color: isDark ? "#e2e8f0" : "#1e293b",
    fontWeight: 600,
    fontSize: "0.9rem",
  }),
  placeholder: (base: any) => ({
    ...base,
    color: isDark ? "#64748b" : "#94a3b8",
    fontWeight: 500,
    fontSize: "0.9rem",
  }),
  menu: (base: any) => ({
    ...base,
    background: isDark ? "#111c44" : "#fff",
    borderRadius: "0.75rem",
    border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "#e2e8f0"}`,
    boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
    zIndex: 9999,
  }),
  menuList: (base: any) => ({
    ...base,
    maxHeight: "200px",
    padding: "4px",
  }),
  option: (base: any, state: any) => ({
    ...base,
    background: state.isSelected
      ? "#3b82f6"
      : state.isFocused
        ? (isDark ? "rgba(59,130,246,0.15)" : "#eff6ff")
        : "transparent",
    color: state.isSelected
      ? "#fff"
      : (isDark ? "#e2e8f0" : "#1e293b"),
    borderRadius: "0.5rem",
    fontWeight: 500,
    fontSize: "0.875rem",
    cursor: "pointer",
    padding: "8px 12px",
  }),
  indicatorSeparator: () => ({ display: "none" }),
  dropdownIndicator: (base: any, state: any) => ({
    ...base,
    color: state.isDisabled
      ? (isDark ? "#475569" : "#94a3b8")
      : (isDark ? "#64748b" : "#64748b"),
    padding: "0 10px",
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "0 12px",
  }),
});

const HotelInsertionForm = ({
  hotelRequest,
  setHotelRequest,
}: Omit<Props, "handleSave" | "loading">) => {
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  // ---- Dark mode detection (Tailwind class-based) ----
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains("dark")
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  // ---- Location State ----
  // name→id map built from GetCountries() — used to call GetState(id)
  const [countryIdMap, setCountryIdMap] = useState<Record<string, number>>({});
  const [stateList, setStateList]       = useState<StateType[]>([]);
  const [cityList, setCityList]         = useState<CityType[]>([]);

  const [selectedCountry, setSelectedCountry] = useState<CountryOption | null>(null);
  const [selectedState,   setSelectedState]   = useState<{ value: number; label: string } | null>(null);
  const [selectedCity,    setSelectedCity]    = useState<{ value: number; label: string } | null>(null);

  // Build name→id map once from library (needed for GetState)
  useEffect(() => {
    GetCountries().then((list: CountryType[]) => {
      const map: Record<string, number> = {};
      list.forEach((c) => { map[c.name.toLowerCase()] = c.id; });
      setCountryIdMap(map);
    });
  }, []);

  // Reset local state when hotelRequest fields are cleared (e.g., after success save)
  useEffect(() => {
    if (!hotelRequest.Country) {
      setSelectedCountry(null);
      setSelectedState(null);
      setSelectedCity(null);
      setStateList([]);
      setCityList([]);
    } else if (!hotelRequest.State) {
      setSelectedState(null);
      setSelectedCity(null);
      setCityList([]);
    } else if (!hotelRequest.City) {
      setSelectedCity(null);
    }
  }, [hotelRequest.Country, hotelRequest.State, hotelRequest.City]);

  // Country options derived from COUNTRY_CODES (for flags)
  const countryOptions: CountryOption[] = COUNTRY_CODES.map((c) => ({
    value: countryIdMap[c.country.toLowerCase()] ?? 0,
    label: c.country,
    flagUrl: c.flagUrl,
  }));

  // Custom renderers for flag + name
  const formatCountryOption = (opt: CountryOption) => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
      <img
        src={opt.flagUrl}
        alt={opt.label}
        style={{ width: 22, height: 16, objectFit: "cover", borderRadius: 2, flexShrink: 0 }}
        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
      />
      <span>{opt.label}</span>
    </div>
  );

  const handleCountryChange = async (opt: CountryOption | null) => {
    setSelectedCountry(opt);
    setSelectedState(null);
    setSelectedCity(null);
    setStateList([]);
    setCityList([]);
    setHotelRequest((prev: any) => ({ ...prev, Country: opt?.label ?? "", State: "", City: "" }));
    if (opt && opt.value) {
      const states = await GetState(opt.value);
      setStateList(states);
    }
  };

  const handleStateChange = async (opt: { value: number; label: string } | null) => {
    setSelectedState(opt);
    setSelectedCity(null);
    setCityList([]);
    setHotelRequest((prev: any) => ({ ...prev, State: opt?.label ?? "", City: "" }));
    if (opt && selectedCountry?.value) {
      const cities = await GetCity(selectedCountry.value, opt.value);
      setCityList(cities);
    }
  };

  const handleCityChange = (opt: { value: number; label: string } | null) => {
    setSelectedCity(opt);
    setHotelRequest((prev: any) => ({ ...prev, City: opt?.label ?? "" }));
  };

  const selectStyles = buildSelectStyles(isDark);

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 1 * 1024 * 1024) {
      toast.error("Security alert: File size exceeds 1MB threshold.");
      e.target.value = "";
      return;
    }

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      toast.error("Format mismatch: Use standard JPG or PNG protocols.");
      e.target.value = "";
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setLogoPreview(previewUrl);

    try {
      const loadingToast = toast.loading("Encrypting and uploading media...");
      const res = await getUploadImageUrl(file);
      toast.dismiss(loadingToast);

      if (res.success && res.data?.id) {
        setHotelRequest((prev: any) => ({ ...prev, LogoUrl: res.data.id }));
        toast.success("Asset verified and stored successfully.");
      } else {
        toast.error("Upload protocol failure.");
        setLogoPreview(null);
      }
    } catch (err) {
      toast.error("Media synchronization error.");
      setLogoPreview(null);
    }
  };

  const handleRequestChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;

    // Strict validation logic
    const numberOnlyFields = ["PostalCode", "GST", "FoodGST", "ServiceCharge"];

    if (numberOnlyFields.includes(name)) {
      if (!/^[0-9]*$/.test(value)) return;
    }

    // Phone number validation (allows digits and +)
    if (name === "Phone") {
      if (!/^[0-9+]*$/.test(value)) return;
      if (value.length > 15) return;
    }

    setHotelRequest((prev: any) => ({
      ...prev,
      [name]: numberOnlyFields.includes(name)
        ? value === ""
          ? ""
          : Number(value)
        : value,
    }));
  };

  const handleRemoveLogo = () => {
    setHotelRequest((prev: any) => ({ ...prev, LogoUrl: "" }));
    setLogoPreview(null);
  };

  useEffect(() => {
    if (!hotelRequest.LogoUrl) {
      setLogoPreview(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [hotelRequest.LogoUrl]);

  return (
    <div className="animate-fadeIn w-full">
      {/* ===================== MAIN FORM BODY ===================== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          {/* Basic Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-[#111c44] rounded-2xl p-4 md:p-6 shadow-sm border border-slate-200 dark:border-white/10 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50/50 dark:bg-blue-900/10 rounded-bl-[80px] -mr-12 -mt-12 transition-colors" />

            <SectionTitle
              icon={Building2}
              title="Identity Matrix"
              description="Core property identification parameters"
            />

            <div className="grid md:grid-cols-2 gap-x-8">
              <InputField
                label="Salesman Code"
                name="SalesmanCode"
                value={hotelRequest.SalesmanCode}
                onChange={handleRequestChange}
                placeholder="Enter salesman code"
                icon={ShieldCheck}
              />
              <InputField
                label="Hotel Name"
                name="HotelName"
                value={hotelRequest.HotelName}
                onChange={handleRequestChange}
                placeholder="e.g. Grand Plaza Royale"
                icon={Building2}
              />
              <InputField
                label="System Username"
                name="Username"
                value={hotelRequest.Username}
                onChange={handleRequestChange}
                placeholder="master_admin"
                icon={FileText}
              />
              <InputField
                label="Access Password"
                name="Password"
                value={hotelRequest.Password}
                onChange={handleRequestChange}
                placeholder="••••••••"
                type={showPassword ? "text" : "password"}
                icon={KeyRound}
                showToggle={true}
                onToggleShow={() => setShowPassword((prev) => !prev)}
              />
            </div>
          </motion.div>

          {/* Location & Contact */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-[#111c44] rounded-2xl p-4 md:p-6 shadow-sm border border-slate-200 dark:border-white/10 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-50/50 dark:bg-indigo-900/10 rounded-bl-[80px] -mr-12 -mt-12 transition-colors" />

            <SectionTitle
              icon={MapPin}
              title="Geographic Node"
              description="Physical presence and digital reach"
            />

            <div className="grid md:grid-cols-2 gap-x-8">
              <div className="md:col-span-2">
                <InputField
                  label="Terminal Address"
                  name="Address"
                  value={hotelRequest.Address}
                  onChange={handleRequestChange}
                  placeholder="Enter physical location address"
                  icon={MapPin}
                  multiline={true}
                />
              </div>
              {/* ── Country ── */}
              <div className="mb-4 space-y-2">
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-1">
                  Country
                </label>
                <Select<CountryOption>
                  styles={selectStyles}
                  options={countryOptions}
                  value={selectedCountry}
                  onChange={handleCountryChange}
                  placeholder="Select Country"
                  isClearable
                  classNamePrefix="csc"
                  formatOptionLabel={formatCountryOption}
                />
              </div>

              {/* ── State ── */}
              <div className="mb-4 space-y-2">
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-1">
                  Regional State
                </label>
                <Select
                  styles={selectStyles}
                  options={stateList.map((s) => ({ value: s.id, label: s.name }))}
                  value={selectedState}
                  onChange={handleStateChange}
                  placeholder={selectedCountry ? "Select State" : "Select a country first"}
                  isDisabled={!selectedCountry}
                  isClearable
                  classNamePrefix="csc"
                />
              </div>

              {/* ── City ── */}
              <div className="mb-4 space-y-2">
                <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-1">
                  City Zone
                </label>
                <Select
                  styles={selectStyles}
                  options={cityList.map((c) => ({ value: c.id, label: c.name }))}
                  value={selectedCity}
                  onChange={handleCityChange}
                  placeholder={selectedState ? "Select City" : "Select a state first"}
                  isDisabled={!selectedState}
                  isClearable
                  classNamePrefix="csc"
                />
              </div>

              <InputField
                label="Postal Code"
                name="PostalCode"
                value={hotelRequest.PostalCode}
                onChange={handleRequestChange}
                placeholder="Postal Code"
              />
              <InputField
                label="Mobile Protocol"
                name="Phone"
                value={hotelRequest.Phone}
                onChange={handleRequestChange}
                placeholder="+91"
                icon={Phone}
              />
              <div className="md:col-span-2">
                <InputField
                  label="Official Email"
                  name="Email"
                  type="email"
                  value={hotelRequest.Email}
                  onChange={handleRequestChange}
                  placeholder="terminal@rooomix.com"
                  icon={Mail}
                />
              </div>
              <div className="md:col-span-2">
                <InputField
                  label="Digital Domain"
                  name="Website"
                  value={hotelRequest.Website}
                  onChange={handleRequestChange}
                  placeholder="https://hotel.com"
                  icon={Globe}
                />
              </div>
            </div>
          </motion.div>

          {/* Tax & Charges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-[#111c44] rounded-2xl p-4 md:p-6 shadow-sm border border-slate-200 dark:border-white/10 relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-bl-[80px] -mr-12 -mt-12 transition-colors" />

            <SectionTitle
              icon={CreditCard}
              title="Financial Schema"
              description="Taxation and balance configuration"
            />

            <div className="grid md:grid-cols-2 gap-x-8">
              <InputField
                label="Tax ID / GST Number"
                name="TaxNumber"
                value={hotelRequest.TaxNumber}
                onChange={handleRequestChange}
                placeholder="Enter Tax ID"
                icon={FileText}
              />
              <InputField
                label="Standard GST (%)"
                name="GST"
                type="number"
                value={hotelRequest.GST}
                onChange={handleRequestChange}
                placeholder="18"
                icon={IndianRupee}
              />
              <InputField
                label="Food & Bev GST (%)"
                name="FoodGST"
                type="number"
                value={hotelRequest.FoodGST}
                onChange={handleRequestChange}
                placeholder="5"
                icon={IndianRupee}
              />

              <div className="mb-4 space-y-2 group">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-widest pl-1">
                  Currency Symbol
                </label>

                <div className="relative group">
                  <select
                    name="CurrencySymbol"
                    value={hotelRequest.CurrencySymbol ?? ""}
                    onChange={handleRequestChange}
                    className="w-full bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-200 dark:border-white/10 rounded-xl pl-4 pr-11 py-3 focus:bg-white dark:focus:bg-[#0b1437] focus:ring-4 focus:ring-blue-600/10 dark:focus:ring-white/5 focus:border-blue-500 dark:focus:border-white/20 outline-none transition-all duration-300 font-medium text-slate-800 dark:text-slate-200 appearance-none drop-shadow-sm"
                  >
                    <option
                      value=""
                      className="dark:bg-[#111c44] dark:text-slate-400"
                    >
                      Select Currency
                    </option>
                    <option
                      value="₹"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ₹ - Indian Rupee (INR)
                    </option>
                    <option
                      value="$"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      $ - US Dollar (USD)
                    </option>
                    <option
                      value="€"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      € - Euro (EUR)
                    </option>
                    <option
                      value="£"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      £ - British Pound (GBP)
                    </option>
                    <option
                      value="¥"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ¥ - Japanese Yen (JPY)
                    </option>
                    <option
                      value="₩"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ₩ - South Korean Won (KRW)
                    </option>
                    <option
                      value="₽"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ₽ - Russian Ruble (RUB)
                    </option>
                    <option
                      value="₺"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ₺ - Turkish Lira (TRY)
                    </option>
                    <option
                      value="₫"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ₫ - Vietnamese Dong (VND)
                    </option>
                    <option
                      value="₪"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ₪ - Israeli Shekel (ILS)
                    </option>
                    <option
                      value="₱"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ₱ - Philippine Peso (PHP)
                    </option>
                    <option
                      value="₦"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ₦ - Nigerian Naira (NGN)
                    </option>
                    <option
                      value="฿"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ฿ - Thai Baht (THB)
                    </option>
                    <option
                      value="₴"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ₴ - Ukrainian Hryvnia (UAH)
                    </option>
                    <option
                      value="₡"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ₡ - Costa Rican Colon (CRC)
                    </option>
                    <option
                      value="₲"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ₲ - Paraguayan Guarani (PYG)
                    </option>
                    <option
                      value="₵"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ₵ - Ghanaian Cedi (GHS)
                    </option>
                    <option
                      value="₸"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ₸ - Kazakhstani Tenge (KZT)
                    </option>
                    <option
                      value="₮"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ₮ - Mongolian Tugrik (MNT)
                    </option>
                    <option
                      value="₭"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ₭ - Laotian Kip (LAK)
                    </option>
                    <option
                      value="د.إ"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      د.إ - UAE Dirham (AED)
                    </option>
                    <option
                      value="﷼"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ﷼ - Saudi Riyal (SAR)
                    </option>
                    <option
                      value="د.ك"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      د.ك - Kuwaiti Dinar (KWD)
                    </option>
                    <option
                      value="د.ب"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      د.ب - Bahraini Dinar (BHD)
                    </option>
                    <option
                      value="ر.ع."
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ر.ع. - Omani Rial (OMR)
                    </option>
                    <option
                      value="ر.ق"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ر.ق - Qatari Riyal (QAR)
                    </option>
                    <option
                      value="₣"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ₣ - Swiss Franc (CHF)
                    </option>
                    <option
                      value="kr"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      kr - Scandinavian Krona
                    </option>
                    <option
                      value="R$"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      R$ - Brazilian Real (BRL)
                    </option>
                    <option
                      value="A$"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      A$ - Australian Dollar (AUD)
                    </option>
                    <option
                      value="C$"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      C$ - Canadian Dollar (CAD)
                    </option>
                    <option
                      value="NZ$"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      NZ$ - New Zealand Dollar (NZD)
                    </option>
                    <option
                      value="S$"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      S$ - Singapore Dollar (SGD)
                    </option>
                    <option
                      value="HK$"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      HK$ - Hong Kong Dollar (HKD)
                    </option>
                    <option
                      value="RM"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      RM - Malaysian Ringgit (MYR)
                    </option>
                    <option
                      value="Rp"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      Rp - Indonesian Rupiah (IDR)
                    </option>
                    <option
                      value="₿"
                      className="dark:bg-[#111c44] dark:text-slate-200"
                    >
                      ₿ - Bitcoin (BTC)
                    </option>
                  </select>
                  <ChevronDown
                    size={18}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 group-focus-within:text-blue-600 dark:group-focus-within:text-blue-400 transition-colors pointer-events-none"
                  />
                </div>
              </div>
              {/* <InputField
                label="Service Protocol (%)"
                name="ServiceCharge"
                type="number"
                value={hotelRequest.ServiceCharge}
                onChange={handleRequestChange}
                placeholder="0"
                icon={IndianRupee}
              /> */}
            </div>
          </motion.div>
        </div>

        {/* SIDEBAR FOR LOGO & PLAN */}
        <div className="lg:col-span-4 space-y-6">
          {/* Floating Branding Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-slate-900 rounded-2xl p-4 md:p-6 shadow-sm text-white relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-blue-600/10 blur-[80px]" />

            <div className="relative z-10 flex items-center gap-3 mb-5">
              <div className="p-3 bg-white/10 border border-white/10 rounded-2xl text-blue-400">
                <UploadCloud size={22} />
              </div>
              <h3 className="text-xl font-black tracking-tight">
                Branding Assets
              </h3>
            </div>

            <div className="relative z-10">
              {logoPreview ? (
                <div className="relative group w-full h-48 rounded-3xl overflow-hidden border border-white/10 shadow-lg">
                  <img
                    src={logoPreview}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-sm">
                    <button
                      onClick={handleRemoveLogo}
                      className="p-3 bg-red-500 text-white rounded-2xl hover:bg-red-600 shadow-xl active:scale-95 transition-all"
                    >
                      <X size={24} />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="relative group w-full h-48 border-2 border-dashed border-white/20 rounded-3xl flex flex-col items-center justify-center text-center p-6 bg-white/5 hover:bg-white/10 hover:border-blue-400/50 transition-all cursor-pointer">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  />
                  {/* <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  /> */}
                  <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center text-blue-400 mb-4 group-hover:scale-110 transition-transform">
                    <UploadCloud size={28} />
                  </div>
                  <p className="text-sm font-bold opacity-80">
                    Upload Brand Logo
                  </p>
                  <p className="text-[10px] opacity-40 uppercase tracking-widest mt-2">
                    Maximum file size: 1MB
                  </p>
                </div>
              )}
            </div>

            <div className="mt-5 relative z-10 space-y-3">
              <div className="flex items-center gap-3 text-xs font-bold text-white/60">
                <CheckCircle2 size={16} className="text-emerald-400" />
                PNG, JPG or WEBP formats
              </div>
              <div className="flex items-center gap-3 text-xs font-bold text-white/60">
                <CheckCircle2 size={16} className="text-emerald-400" />
                Optimized for white background
              </div>
            </div>
          </motion.div>

          {/* License & Plan */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white dark:bg-[#111c44] rounded-2xl p-4 md:p-6 shadow-sm border border-slate-200 dark:border-white/10 relative overflow-hidden"
          >
            <div className="flex items-center gap-3 mb-5">
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-2xl text-amber-600 dark:text-amber-400">
                <KeyRound size={22} />
              </div>
              <h3 className="text-lg font-black text-slate-800 dark:text-slate-100 tracking-tight">
                Operation Plan
              </h3>
            </div>

            <div className="space-y-3">
              <InputField
                label="Enforcement Key"
                name="LicenceKey"
                value={hotelRequest.LicenceKey}
                onChange={handleRequestChange}
                placeholder="Enter Key"
                icon={Lock}
              />
              <InputField
                label="License Expiry"
                name="ProExpireDate"
                type="date"
                value={hotelRequest.ProExpireDate}
                onChange={handleRequestChange}
                icon={null}
              />

              <div className="grid grid-cols-2 gap-4">
                <InputField
                  label="Check-In"
                  name="CheckInTime"
                  type="time"
                  value={hotelRequest.CheckInTime}
                  onChange={handleRequestChange}
                  icon={null}
                />
                <InputField
                  label="Check-Out"
                  name="CheckOutTime"
                  type="time"
                  value={hotelRequest.CheckOutTime}
                  onChange={handleRequestChange}
                  icon={null}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default HotelInsertionForm;
